import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Database, Loader2, Plug, RefreshCw, Settings2, CreditCard } from 'lucide-react'
import type { McpConfigPut, McpServerId, McpServerStatus, McpStatus } from '@one-deployment/api'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Separator } from '@/components/shadcn/separator'
import { Switch } from '@/components/shadcn/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { UploadsList } from '@/features/workspace/chat/UploadsPanel'
import { ErrorState } from '@/components/status'
import { SkeletonRows } from '@/components/ui'
import { useCan } from '@/components/RoleGate'
import { enc, get, put } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'

/** Checked again every minute, and whenever the window comes back into view. */
export function useMcp(project: string, enabled = true) {
  return useQuery({
    queryKey: qk.mcp(project),
    queryFn: ({ signal }) => get<McpStatus>(`/api/projects/${enc(project)}/mcp`, signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled,
  })
}

const ICON: Record<McpServerId, React.ReactNode> = { supabase: <Database className="size-4" />, stripe: <CreditCard className="size-4" /> }
const up = (s: McpServerStatus) => s.state === 'connected'
const down = (s: McpServerStatus) => s.state === 'error' || s.state === 'missing'

function Dot({ s, className }: { s: McpServerStatus['state']; className?: string }) {
  return <span className={cn('inline-block size-2 shrink-0 rounded-full', s === 'connected' ? 'bg-ok' : s === 'off' ? 'bg-fg-faint' : 'bg-err', className)} />
}

function StateBadge({ s }: { s: McpServerStatus }) {
  const label = { connected: 'Connected', error: 'Disconnected', missing: 'Needs setup', off: 'Off' }[s.state]
  return (
    <Badge variant="secondary" className={cn('gap-1.5 rounded-full text-[10px]', s.state === 'connected' ? 'bg-ok/10 text-ok' : s.state === 'off' ? 'text-muted-foreground' : 'bg-err/10 text-err')}>
      <Dot s={s.state} className="size-1.5" />{label}
    </Badge>
  )
}

/**
 * The MCP button in a session's window header: a green dot and how many
 * servers are connected, a red one and how many are not. Opening it lists
 * each with why, a way to check again, and the settings.
 */
export function McpButton({ project, sessionId }: { project: string; sessionId?: string }) {
  const q = useMcp(project)
  const servers = q.data?.servers.filter((s) => s.enabled) ?? []
  const ok = servers.filter(up).length
  const bad = servers.filter(down).length
  return (
    <Popover onOpenChange={(o) => { if (o) void q.refetch() }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs" aria-label={`Settings. MCP servers: ${ok} connected, ${bad} disconnected`} title="Settings"
          className="h-6 gap-1.5 rounded-sm px-1.5 font-normal text-fg-subtle hover:bg-hover has-[>svg]:px-1.5">
          <Settings2 size={11} />
          {ok > 0 && <span className="flex items-center gap-1 tabular-nums"><Dot s="connected" className="size-1.5" />{ok}</span>}
          {bad > 0 && <span className="flex items-center gap-1 tabular-nums text-err"><Dot s="error" className="size-1.5" />{bad}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <Tabs defaultValue="mcp" className="gap-0">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="text-sm font-medium">Settings</div>
            <TabsList className="ml-auto h-7">
              <TabsTrigger value="mcp" className="gap-1.5 px-2 text-xs">MCP
                {servers.length > 0 && <span className={cn('rounded-full px-1.5 text-[10px] tabular-nums', bad ? 'bg-err/15 text-err' : 'bg-ok/15 text-ok')}>{bad ? bad : ok}</span>}
              </TabsTrigger>
              {sessionId && <TabsTrigger value="uploads" className="px-2 text-xs">Uploads</TabsTrigger>}
            </TabsList>
          </div>
          <Separator />
          <TabsContent value="mcp">
            <div className="flex items-center justify-end gap-1 px-3 pt-2">
              <Button variant="ghost" size="icon-xs" title="Check again" aria-label="Check again" disabled={q.isFetching} onClick={() => void q.refetch()}>
                <RefreshCw className={cn('size-3.5', q.isFetching && 'animate-spin')} />
              </Button>
              <Button asChild variant="ghost" size="xs" className="gap-1">
                <Link to="/p/$project/settings/$section" params={{ project, section: 'claude' }}><Settings2 className="size-3.5" />Set up</Link>
              </Button>
            </div>
            {q.error && !q.data ? <div className="p-3 text-xs text-err">{(q.error as Error).message}</div>
              : !q.data ? <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" />Checking…</div>
              : (
                <ul className="flex flex-col py-1">
                  {q.data.servers.map((s) => (
                    <li key={s.id} className="flex items-start gap-2.5 px-3 py-2">
                      <span className="mt-0.5 text-muted-foreground">{ICON[s.id]}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[13px] font-medium">{s.name}<StateBadge s={s} /></div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{s.detail}</div>
                      </div>
                      {down(s) && <Button variant="outline" size="xs" className="shrink-0" disabled={q.isFetching} onClick={() => void q.refetch()}>Reconnect</Button>}
                    </li>
                  ))}
                </ul>
              )}
          </TabsContent>
          {sessionId && <TabsContent value="uploads"><UploadsList sessionId={sessionId} /></TabsContent>}
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}

/** Settings → Claude Code integrations: Supabase and Stripe MCP servers on production. */
export function McpSettings({ project }: { project: string }) {
  const q = useMcp(project)
  if (q.error && !q.data) return <ErrorState error={q.error} retry={() => void q.refetch()} />
  if (!q.data) return <SkeletonRows rows={6} />
  return <McpForm project={project} data={q.data} checking={q.isFetching} recheck={() => void q.refetch()} />
}

function McpForm({ project, data, checking, recheck }: { project: string; data: McpStatus; checking: boolean; recheck: () => void }) {
  const qc = useQueryClient()
  const canEdit = useCan('project.secrets.write', project)
  const [cfg, setCfg] = useState(data.config)
  const [ref, setRef] = useState(data.config.supabase.ref ?? '')
  const [supabaseToken, setSupabaseToken] = useState('')
  const [stripeKey, setStripeKey] = useState('')
  const save = useMutation({
    mutationFn: (b: McpConfigPut) => put<McpStatus>(`/api/projects/${enc(project)}/mcp`, b),
    onSuccess: () => { setSupabaseToken(''); setStripeKey(''); void qc.invalidateQueries({ queryKey: qk.mcp(project) }) },
  })
  const status = (id: McpServerId) => data.servers.find((s) => s.id === id)!
  const submit = (extra: Partial<McpConfigPut> = {}) => save.mutate({
    supabase: { ...cfg.supabase, ref: ref.trim() || null },
    stripe: cfg.stripe,
    ...(supabaseToken.trim() ? { supabaseToken } : {}),
    ...(stripeKey.trim() ? { stripeKey } : {}),
    ...extra,
  })
  const dirty = JSON.stringify(cfg) !== JSON.stringify(data.config) || (ref.trim() || null) !== data.config.supabase.ref || !!supabaseToken.trim() || !!stripeKey.trim()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-medium">Claude Code integrations</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            MCP servers every Claude Code session in this project gets, robots' included, on <span className="text-foreground">production</span>.
            Keys are stored encrypted and never shown again. A save reaches running sessions at once: their Claude restarts on the same conversation.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" disabled={checking} onClick={recheck}><RefreshCw className={cn('size-3.5', checking && 'animate-spin')} />Check now</Button>
      </div>

      <Server s={status('supabase')} title="Supabase" description="Claude can query the production database, read logs and inspect the schema through Supabase's hosted MCP server."
        enabled={cfg.supabase.enabled} disabled={!canEdit} onEnabled={(v) => setCfg({ ...cfg, supabase: { ...cfg.supabase, enabled: v } })}>
        <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
          <div>
            <Label htmlFor="mcp-ro" className="text-sm">Read-only</Label>
            <p className="text-xs text-muted-foreground">Recommended. Off lets Claude write to the production database.</p>
          </div>
          <Switch id="mcp-ro" checked={cfg.supabase.readOnly} disabled={!canEdit} onCheckedChange={(v) => setCfg({ ...cfg, supabase: { ...cfg.supabase, readOnly: v } })} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="mcp-ref">Project ref</Label>
          <Input id="mcp-ref" className="font-mono" placeholder="Empty: the production Supabase ref from General" value={ref} disabled={!canEdit} onChange={(e) => setRef(e.target.value)} />
        </div>
        <KeyField id="mcp-supa" label="Access token" placeholder="sbp_…" value={supabaseToken} onChange={setSupabaseToken} s={status('supabase')} disabled={!canEdit}
          hint={<>A personal access token from supabase.com/dashboard/account/tokens. Empty uses the Supabase account picked for this project on Connections.</>}
          onRemove={status('supabase').keySource === 'secret' ? () => submit({ supabaseToken: '' }) : undefined} />
      </Server>

      <Server s={status('stripe')} title="Stripe" description="Claude can look up customers, payments, subscriptions and products in the live account through Stripe's hosted MCP server."
        enabled={cfg.stripe.enabled} disabled={!canEdit} onEnabled={(v) => setCfg({ ...cfg, stripe: { enabled: v } })}>
        <KeyField id="mcp-stripe" label="Live key" placeholder="rk_live_…" value={stripeKey} onChange={setStripeKey} s={status('stripe')} disabled={!canEdit}
          hint={<>A restricted key (rk_live_) with only the access Claude needs is safest. A secret key (sk_live_) gives it everything.</>}
          onRemove={status('stripe').keyLast4 ? () => submit({ stripeKey: '' }) : undefined} />
      </Server>

      <div className="flex items-center gap-3">
        <Button size="sm" className="self-start" disabled={!canEdit || !dirty || save.isPending} onClick={() => submit()}>{save.isPending ? 'Saving…' : 'Save'}</Button>
        {save.error && <span className="text-sm text-err">{(save.error as Error).message}</span>}
        {!canEdit && <span className="text-sm text-muted-foreground">Only people who can change secrets can change these.</span>}
      </div>
    </div>
  )
}

function Server({ s, title, description, enabled, disabled, onEnabled, children }: {
  s: McpServerStatus; title: string; description: string; enabled: boolean; disabled: boolean; onEnabled: (v: boolean) => void; children: React.ReactNode
}) {
  return (
    <Card className="gap-4 rounded-lg border-border bg-surface-2 py-4 shadow-none">
      <CardHeader className="flex flex-row items-start gap-3 px-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-surface-3 text-muted-foreground">{ICON[s.id]}</span>
        <div className="min-w-0 flex-1">
          <CardTitle className="flex items-center gap-2 text-sm">{title}<StateBadge s={s} /></CardTitle>
          <CardDescription className="mt-1 text-xs">{description}</CardDescription>
          {s.state !== 'off' && <p className={cn('mt-1.5 text-xs', up(s) ? 'text-ok' : 'text-err')}>{s.detail}</p>}
        </div>
        <Switch checked={enabled} disabled={disabled} onCheckedChange={onEnabled} aria-label={`Use the ${title} MCP server`} />
      </CardHeader>
      {enabled && <CardContent className="flex flex-col gap-3 px-4">{children}</CardContent>}
    </Card>
  )
}

function KeyField({ id, label, placeholder, value, onChange, s, hint, disabled, onRemove }: {
  id: string; label: string; placeholder: string; value: string; onChange: (v: string) => void; s: McpServerStatus; hint: React.ReactNode; disabled: boolean; onRemove?: () => void
}) {
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center gap-2">
        <Label htmlFor={id}>{label}</Label>
        {s.keyLast4 && <span className="font-mono text-xs text-muted-foreground">in use: ••••{s.keyLast4}{s.keySource === 'connection' ? ' (from Connections)' : ''}</span>}
        {onRemove && <Button variant="link" size="xs" className="ml-auto h-auto p-0 text-xs text-muted-foreground" disabled={disabled} onClick={onRemove}>Remove key</Button>}
      </div>
      <Input id={id} type="password" autoComplete="off" className="font-mono" placeholder={s.keyLast4 ? 'Leave empty to keep the key in use' : placeholder} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}
